import { useState, useEffect, useMemo } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faBox,
  faCube,
  faCalendarPlus,
  faMoneyBillWave,
  faChartLine,
  faArrowTrendUp,
  faArrowTrendDown,
  faSpinner,
  faBoxes,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";
import { useProducts } from "../../../hooks/useProducts";

const AddStockModal = ({ isOpen, onClose, onAdd, shopId }) => {
  const { products, productsLoading, refreshProducts } = useProducts();

  const [formData, setFormData] = useState({
    productId: "",
    dozen_amount: "",
    purchase_price: "",
    selling_price: "",
    purchase_date: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        productId: "",
        dozen_amount: "",
        purchase_price: "",
        selling_price: "",
        purchase_date: "",
      });
      setErrors({});
      setIsSubmitting(false);
      refreshProducts();
    }
  }, [isOpen, refreshProducts]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  // Get selected product details
  const selectedProduct = useMemo(() => {
    if (!formData.productId) return null;
    return products?.find((p) => p.productId === formData.productId);
  }, [products, formData.productId]);

  // Get package type display name
  const getPackageDisplayName = (packageType) => {
    if (!packageType) return "Package";
    const packageMap = {
      dozen: "Dozen",
      packet: "Packet",
      box: "Box",
      carton: "Carton",
      bundle: "Bundle",
      bag: "Bag",
      jar: "Jar",
      bottle: "Bottle",
      piece: "Piece",
      kg: "Kilogram",
      gram: "Gram",
      liter: "Liter",
      ml: "Milliliter",
    };
    return packageMap[packageType.toLowerCase()] || packageType;
  };

  // Calculate totals with correct logic
  const calculateTotals = () => {
    const dozenAmount = parseFloat(formData.dozen_amount) || 0;
    const totalPurchase = parseFloat(formData.purchase_price) || 0;
    const singleSelling = parseFloat(formData.selling_price) || 0;

    // Get items per package from selected product
    const itemsPerPackage = selectedProduct?.no_of_items || 0;

    // Calculate total items
    const totalItems = dozenAmount * itemsPerPackage;

    // Calculate cost per item
    const costPerItem = totalItems > 0 ? totalPurchase / totalItems : 0;

    // Calculate total selling price
    const totalSelling = totalItems * singleSelling;

    // Calculate expected profit/loss
    const expectedProfit = totalSelling - totalPurchase;

    // Calculate profit per item
    const profitPerItem = singleSelling - costPerItem;

    // Calculate profit margin
    const profitMargin =
      totalPurchase > 0 ? (expectedProfit / totalPurchase) * 100 : 0;

    return {
      dozenAmount,
      itemsPerPackage,
      totalItems,
      costPerItem,
      totalPurchase,
      totalSelling,
      expectedProfit,
      profitPerItem,
      profitMargin,
      isProfit: expectedProfit >= 0,
      isLoss: expectedProfit < 0,
    };
  };

  const {
    dozenAmount,
    itemsPerPackage,
    totalItems,
    costPerItem,
    totalPurchase,
    totalSelling,
    expectedProfit,
    profitPerItem,
    profitMargin,
    isProfit,
    isLoss,
  } = calculateTotals();

  const handleSubmit = async () => {
    const newErrors = {};

    if (!formData.productId) {
      newErrors.productId = "Please select a product";
    }
    if (!formData.dozen_amount || parseFloat(formData.dozen_amount) <= 0) {
      newErrors.dozen_amount = `Please enter valid ${getPackageDisplayName(selectedProduct?.package_type || "package")} amount`;
    }
    if (!formData.purchase_price || parseFloat(formData.purchase_price) <= 0) {
      newErrors.purchase_price = "Please enter a valid purchase price";
    }
    if (!formData.selling_price || parseFloat(formData.selling_price) <= 0) {
      newErrors.selling_price = "Please enter a valid selling price";
    }
    if (!formData.purchase_date) {
      newErrors.purchase_date = "Please select a purchase date";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setIsSubmitting(true);
      await onAdd({
        productId: formData.productId,
        dozen_amount: parseFloat(formData.dozen_amount),
        purchase_price: parseFloat(formData.purchase_price),
        selling_price: parseFloat(formData.selling_price),
        purchase_date: formData.purchase_date,
        shopId: shopId,
      });
      setFormData({
        productId: "",
        dozen_amount: "",
        purchase_price: "",
        selling_price: "",
        purchase_date: "",
      });
      setErrors({});
      onClose();
    } catch (error) {
      console.error("Failed to add stock:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (n) => `TZS ${Number(n).toLocaleString()}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Stock"
      maxWidth="sm:max-w-lg"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="h-4 w-4 animate-spin"
                />
                Adding...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                Add Stock
              </>
            )}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Info Banner */}
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <FontAwesomeIcon icon={faBox} className="text-blue-800 h-4 w-4" />
          </div>
          <div>
            <p className="text-xs text-gray-600 font-semibold">
              Add stock to shop inventory
            </p>
            <p className="text-xs text-gray-600">
              Fill in all required fields (*)
            </p>
          </div>
        </div>

        {/* Product Selection */}
        <div className="flex flex-col gap-1">
          <label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faBoxes}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Product Name *
          </label>
          <select
            name="productId"
            value={formData.productId}
            onChange={handleChange}
            disabled={productsLoading || isSubmitting}
            className={`flex h-10 w-full rounded-md border ${
              errors.productId ? "border-red-500" : "border-gray-300"
            } bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
          >
            <option value="">Select a product</option>
            {products?.map((product) => (
              <option key={product.productId} value={product.productId}>
                {product.product_name} - {product.package_type || "N/A"} (
                {product.no_of_items || 0} items)
              </option>
            ))}
          </select>
          {productsLoading && (
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <FontAwesomeIcon
                icon={faSpinner}
                className="h-3 w-3 animate-spin"
              />
              Loading products...
            </p>
          )}
          {errors.productId && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.productId}
            </p>
          )}
        </div>

        {/* Product Details - Show when product is selected */}
        {selectedProduct && (
          <div className="grid grid-cols-3 gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div>
              <p className="text-xs text-gray-500">Package Type</p>
              <p className="text-sm font-semibold text-gray-700 capitalize">
                {getPackageDisplayName(selectedProduct.package_type)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">
                Items per {getPackageDisplayName(selectedProduct.package_type)}
              </p>
              <p className="text-sm font-semibold text-gray-700">
                {selectedProduct.no_of_items || 0}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Items</p>
              <p className="text-sm font-semibold text-blue-600">
                {dozenAmount > 0
                  ? dozenAmount * (selectedProduct.no_of_items || 0)
                  : 0}
              </p>
            </div>
          </div>
        )}

        {/* Dozen Amount - matches database field name */}
        <div className="flex flex-col gap-1">
          <label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faCube}
              className="text-blue-800 h-3.5 w-3.5"
            />
            {selectedProduct?.package_type
              ? `${getPackageDisplayName(selectedProduct.package_type)} Amount *`
              : "Amount *"}
          </label>
          <div className="relative">
            <Input
              name="dozen_amount"
              type="number"
              min="0"
              step="1"
              value={formData.dozen_amount}
              onChange={handleChange}
              disabled={isSubmitting || !selectedProduct}
              placeholder={
                selectedProduct
                  ? `Enter ${getPackageDisplayName(selectedProduct.package_type)} amount`
                  : "Select a product first"
              }
              className={`border ${
                errors.dozen_amount ? "border-red-500" : "border-gray-300"
              } rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30 ${
                !selectedProduct ? "bg-gray-50 cursor-not-allowed" : ""
              }`}
            />
            {selectedProduct && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                {getPackageDisplayName(selectedProduct.package_type)}
              </span>
            )}
          </div>
          {errors.dozen_amount && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.dozen_amount}
            </p>
          )}
        </div>

        {/* Purchase & Selling Price */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-gray-700 font-medium flex items-center gap-2">
              <FontAwesomeIcon
                icon={faMoneyBillWave}
                className="text-blue-800 h-3.5 w-3.5"
              />
              Purchase Price (single) *
            </label>
            <div className="relative">
              <Input
                name="purchase_price"
                type="number"
                min="0"
                step="100"
                value={formData.purchase_price}
                onChange={handleChange}
                disabled={isSubmitting}
                placeholder="Enter single purchase price"
                className={`border ${
                  errors.purchase_price ? "border-red-500" : "border-gray-300"
                } rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                Total
              </span>
            </div>
            {errors.purchase_price && (
              <p className="text-red-500 text-xs flex items-center gap-1">
                <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                {errors.purchase_price}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-gray-700 font-medium flex items-center gap-2">
              <FontAwesomeIcon
                icon={faChartLine}
                className="text-blue-800 h-3.5 w-3.5"
              />
              Selling Price (Single) *
            </label>
            <div className="relative">
              <Input
                name="selling_price"
                type="number"
                min="0"
                step="100"
                value={formData.selling_price}
                onChange={handleChange}
                disabled={isSubmitting}
                placeholder="Enter selling price per item"
                className={`border ${
                  errors.selling_price ? "border-red-500" : "border-gray-300"
                } rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                Per Item
              </span>
            </div>
            {errors.selling_price && (
              <p className="text-red-500 text-xs flex items-center gap-1">
                <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                {errors.selling_price}
              </p>
            )}
          </div>
        </div>

        {/* Purchase Date */}
        <div className="flex flex-col gap-1">
          <label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faCalendarPlus}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Purchase Date *
          </label>
          <Input
            name="purchase_date"
            type="date"
            value={formData.purchase_date}
            onChange={handleChange}
            disabled={isSubmitting}
            className={`border ${
              errors.purchase_date ? "border-red-500" : "border-gray-300"
            } rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
          />
          {errors.purchase_date && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.purchase_date}
            </p>
          )}
        </div>

        {/* Smart Profit/Loss Preview */}
        {formData.dozen_amount &&
          formData.purchase_price &&
          formData.selling_price &&
          selectedProduct && (
            <div
              className={`p-3 rounded-lg border ${
                isProfit
                  ? "bg-green-50 border-green-200"
                  : isLoss
                    ? "bg-red-50 border-red-200"
                    : "bg-gray-50 border-gray-200"
              }`}
            >
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    Total Items:
                  </span>
                  <span className="text-lg font-bold text-gray-900">
                    {totalItems} items
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    Cost per Item:
                  </span>
                  <span className="text-lg font-bold text-gray-900">
                    {formatCurrency(costPerItem)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    Total Purchase:
                  </span>
                  <span className="text-lg font-bold text-gray-900">
                    {formatCurrency(totalPurchase)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    Total Selling:
                  </span>
                  <span className="text-lg font-bold text-gray-900">
                    {formatCurrency(totalSelling)}
                  </span>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                  <span className="text-sm font-medium text-gray-700">
                    Expected{" "}
                    {isProfit ? "Profit" : isLoss ? "Loss" : "Break Even"}:
                  </span>
                  <span
                    className={`text-lg font-bold ${
                      isProfit
                        ? "text-green-600"
                        : isLoss
                          ? "text-red-600"
                          : "text-gray-600"
                    }`}
                  >
                    {isProfit ? "+" : isLoss ? "-" : ""}
                    {formatCurrency(Math.abs(expectedProfit))}
                    <span className="ml-2 text-xs">
                      ({profitMargin.toFixed(1)}%)
                    </span>
                    {isProfit && (
                      <span className="ml-2 text-xs font-medium text-green-600">
                        <FontAwesomeIcon
                          icon={faArrowTrendUp}
                          className="mr-1"
                        />
                        Profit
                      </span>
                    )}
                    {isLoss && (
                      <span className="ml-2 text-xs font-medium text-red-600">
                        <FontAwesomeIcon
                          icon={faArrowTrendDown}
                          className="mr-1"
                        />
                        Loss
                      </span>
                    )}
                  </span>
                </div>

                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>
                    {dozenAmount}{" "}
                    {getPackageDisplayName(selectedProduct.package_type)} ×{" "}
                    {itemsPerPackage} items
                  </span>
                  <span className="font-medium">
                    Profit per item: {formatCurrency(profitPerItem)}
                  </span>
                </div>
              </div>
            </div>
          )}
      </div>
    </Modal>
  );
};

export default AddStockModal;
